import { MenuBar, MenuItem } from "@xapp/components";
import { TemplateType } from "@xapp/ovai-lib";
import { Role } from "@xapp/stentor-api-client/dist/models/Roles";
import { StentorAdmin } from "auth/modules/roles";
import { UserProfile } from "auth/modules/state";
import { App } from "generated/graphql";
import React, { FC, useCallback, useMemo } from "react";
import { hasActiveChildren, showNavItem } from "./utils";

export interface NavItem {
    /**
     * Show the item if the user has the provided roles.
     */
    readonly showWithRoles?: (Role | StentorAdmin)[];
    /**
     * Show the item if the app has the provided template types.
     */
    readonly showWithTemplateTypes?: (TemplateType | string)[];
    /**
     * Show the item if the app has the provided beta flags set.
     */
    readonly showWithBetaFlags?: string[];
    /**
     * It will not show the item if it matches the template type.  Can only be overridden by showWithRoles
     */
    readonly doNotShowWithTemplateTypes?: (TemplateType | string)[];
    /**
     * The display name for the item.
     */
    readonly name: string;
    /**
     * The path for the item.
     */
    readonly path?: string;
}

export interface SecondaryNavItem extends NavItem {
    readonly submenuItems?: readonly NavItem[];
}

export interface SecondaryNavProps {
    readonly app?: Pick<App, "templateType" | "beta" | "appId">;
    readonly asUser?: boolean;
    readonly items: readonly SecondaryNavItem[];
    readonly navType: "admin" | "app" | "org";
    readonly location: string;
    readonly user: UserProfile;
    readonly organizationId?: string;
}

export const SecondaryNavView: FC<SecondaryNavProps> = (props: SecondaryNavProps) => {
    const { app, asUser, organizationId, user, items, location } = props;

    const filter = useCallback((item: SecondaryNavItem) => {
        const tellMeTo = showNavItem(item, user, organizationId, app, asUser);

        return tellMeTo.show;

    }, [user, app, organizationId, asUser]);

    const mapItem = useCallback<(item: SecondaryNavItem) => MenuItem>(item => {
        function map(itemRaw: SecondaryNavItem): MenuItem {

            const tellMeTo = showNavItem(itemRaw, user, organizationId, app, asUser);

            return {
                active: hasActiveChildren(itemRaw, location),
                children: itemRaw.submenuItems?.filter(filter).map(map) ?? [],
                label: tellMeTo.showingForAdmin ? `🛠️ ${itemRaw.name}` : itemRaw.name,
                url: itemRaw.path,
            };
        }
        return map(item);
    }, [app, asUser, filter, location, organizationId, user]);

    const res = useMemo<readonly MenuItem[]>(() => items.filter(filter).map(mapItem), [items, filter, mapItem]);

    return (
        <MenuBar items={res} />
    );
};

export default SecondaryNavView;
